import React from "react";
import { useTranslations } from "next-intl";
import { parseNum } from "../constants";
import Field from "./Field";
import SectionTitle from "./SectionTitle";
import FilterDropdown from "./FilterDropdown";

const TradeModalSellForm = ({
  Icon,
  fieldErrors,
  selectedProduct,
  sell,
  setSell,
  sellMinQuantity,
  sellProducts,
  inp,
  CLS,
  setFieldErrors,
  setSellField
}: {
  Icon: {
    box: React.ReactNode;
    user: React.ReactNode;
    payment: React.ReactNode;
    arrowUp: React.ReactNode;
    arrowDown: React.ReactNode;
    arrowRight: React.ReactNode;
    close: React.ReactNode;
    whatsapp: React.ReactNode;
  };
  fieldErrors: Record<string, string>;
  selectedProduct: {
    id: string;
    name: string;
  } | null | undefined;
  sell: {
    product: string;
    pricePerKg: string;
    quantity: string;
    fullName: string;
    phone: string;
    gst: string;
    paymentDetails: string;
  };
  setSell: React.Dispatch<
    React.SetStateAction<{
      product: string;
      pricePerKg: string;
      quantity: string;
      fullName: string;
      phone: string;
      gst: string;
      paymentDetails: string;
    }>
  >;
  sellMinQuantity: number;
  sellProducts: { id: string; name: string }[];
  inp: (error?: string) => string;
  CLS: { input: string };
  setFieldErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setSellField: (
    key: "product" | "quantity" | "fullName" | "gst" | "paymentDetails" | "phone",
  ) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement, Element>) => void;
}) => {
  const t = useTranslations("tradeModal");
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <SectionTitle icon={Icon.box}>
            {t("sell.sections.productDetails")}
          </SectionTitle>
          <div className="space-y-2">
            <Field
              label={t("sell.fields.selectProduct")}
              required
              error={fieldErrors.product}
            >
              {selectedProduct ? (
                <input
                  value={
                    selectedProduct.name.charAt(0).toUpperCase() +
                    selectedProduct.name.slice(1)
                  }
                  readOnly
                  className={`${CLS.input} bg-slate-50 cursor-not-allowed text-slate-600`}
                />
              ) : (
                <FilterDropdown
                  label=""
                  value={sell.product}
                  placeholder={t("sell.placeholders.chooseProductType")}
                  options={[
                    {
                      value: "",
                      label: t("sell.placeholders.chooseProductType"),
                    },
                    ...sellProducts.map((p) => ({
                      value: p.id,
                      label: p.name.charAt(0).toUpperCase() + p.name.slice(1),
                    })),
                  ]}
                  onChange={(value) => {
                    setSellField("product")({
                      target: { value },
                    } as React.ChangeEvent<HTMLSelectElement>);
                  }}
                />
              )}
            </Field>

            <Field
              label={t("sell.fields.quantityKg")}
              required
              error={fieldErrors.quantity}
            >
              <input
                type="number"
                min={sellMinQuantity}
                step={sellMinQuantity}
                value={sell.quantity}
                onChange={(e) => {
                  const raw = e.target.value;
                  setSell((prev) => ({ ...prev, quantity: raw }));
                  const parsed = Number.parseInt(raw, 10);
                  if (!raw || Number.isNaN(parsed)) {
                    setFieldErrors((prev) => ({ ...prev, quantity: "" }));
                    return;
                  }
                  if (parsed <= 0) {
                    setFieldErrors((prev) => ({
                      ...prev,
                      quantity: t("errors.quantityInvalid"),
                    }));
                  } else if (
                    sellMinQuantity > 1 &&
                    parsed % sellMinQuantity !== 0
                  ) {
                    setFieldErrors((prev) => ({
                      ...prev,
                      quantity: t("errors.quantityMultiple", {
                        min: sellMinQuantity,
                        ex1: sellMinQuantity * 2,
                        ex2: sellMinQuantity * 3,
                      }),
                    }));
                  } else {
                    setFieldErrors((prev) => ({ ...prev, quantity: "" }));
                  }
                }}
                onBlur={() => {
                  const parsed = Number.parseInt(sell.quantity, 10);
                  if (!Number.isFinite(parsed) || parsed <= 0) {
                    setSell((prev) => ({
                      ...prev,
                      quantity: String(sellMinQuantity),
                    }));
                    setFieldErrors((prev) => ({ ...prev, quantity: "" }));
                    return;
                  }
                  if (sellMinQuantity > 1) {
                    const snapped = Math.max(
                      sellMinQuantity,
                      Math.round(parsed / sellMinQuantity) * sellMinQuantity,
                    );
                    setSell((prev) => ({ ...prev, quantity: String(snapped) }));
                  }
                  setFieldErrors((prev) => ({ ...prev, quantity: "" }));
                }}
                placeholder={t("sell.placeholders.quantityKg")}
                className={inp(fieldErrors.quantity)}
              />
            </Field>
            <Field
              label={t("sell.fields.pricePerKg")}
              error={fieldErrors.pricePerKg}
            >
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                  ₹
                </span>
                <input
                  value={sell.pricePerKg}
                  readOnly
                  disabled
                  placeholder={t("sell.placeholders.pricePerKg")}
                  className={`${CLS.input} pl-7 bg-slate-200 disabled:cursor-not-allowed text-slate-500`}
                />
              </div>
              {sell.pricePerKg && sell.quantity && (
                <p className="mt-1 text-xs text-slate-500">
                  {t("totalWithGST", {
                    total: (
                      parseNum(sell.pricePerKg) * parseNum(sell.quantity)
                    ).toFixed(2),
                    grandTotal: (
                      parseNum(sell.pricePerKg) *
                      parseNum(sell.quantity) *
                      1.05
                    ).toFixed(2),
                  })}
                </p>
              )}
            </Field>
          </div>
        </div>
        <div>
          <SectionTitle icon={Icon.user}>
            {t("sell.sections.sellerInfo")}
          </SectionTitle>
          <div className="space-y-2">
            <Field
              label={t("sell.fields.fullName")}
              required
              error={fieldErrors.fullName}
            >
              <input
                value={sell.fullName}
                onChange={setSellField("fullName")}
                placeholder={t("sell.placeholders.fullName")}
                className={inp(fieldErrors.fullName)}
              />
            </Field>
            <Field
              label={t("sell.fields.phone")}
              required
              error={fieldErrors.phone}
            >
              <input
                value={sell.phone}
                maxLength={10}
                onChange={(e) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                  setSellField("phone")(e);
                }}
                placeholder={t("sell.placeholders.phone")}
                className={inp(fieldErrors.phone)}
              />
            </Field>
            <Field label={t("sell.fields.gst")}>
              <input
                value={sell.gst}
                onChange={setSellField("gst")}
                placeholder={t("sell.placeholders.gst")}
                className={CLS.input}
              />
            </Field>
          </div>
        </div>
      </div>
      <div>
        <SectionTitle icon={Icon.payment}>
          {t("sell.sections.paymentDetails")}
        </SectionTitle>
        <Field
          label={t("sell.fields.paymentDetails")}
          error={fieldErrors.paymentDetails}
        >
          <textarea
            value={sell.paymentDetails}
            onChange={setSellField("paymentDetails")}
            placeholder={t("sell.placeholders.paymentDetails")}
            rows={2}
            className={CLS.input}
          />
        </Field>
      </div>
    </div>
  );
};

export default TradeModalSellForm;
